<template>
  <div class="project">
    <SearchTable
      ref="SearchTableRef"
      :columns="tableColumns"
      :default-sort="{ prop: 'create_time', order: 'descending' }"
      :api="fetchList"
      :getRows="getRows"
    >
      <template #action="{ scope }">
        <el-space>
          <el-button size="small"
            @click="handleDownload(scope.row)"
          >
            下载
          </el-button>
          <el-button size="small" type="primary" plain v-if="scope.row.isProject && Number(scope.row.status) === 4 && scope.row.resultFiles?.length" @click="handleWriting(scope.row.id)">查看</el-button>
          <el-button size="small" type="primary" v-else-if="scope.row.isProject" @click="handleWriting(scope.row.id)">继续</el-button>
          <el-popconfirm
            width="200"
            title="确定删除该项目?"
            v-if="scope.row.isProject"
            @confirm="handleDelete(scope.row.id)"
          >
            <template #reference>
              <el-button size="small" type="danger">删除</el-button>
            </template>
            <template #actions="{ confirm, cancel }">
              <el-button size="small" @click="cancel">取消</el-button>
              <el-button
                type="danger"
                size="small"
                @click="confirm"
              >
                删除
              </el-button>
            </template>
          </el-popconfirm>
        </el-space>
      </template>
    </SearchTable>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue'
import SearchTable from '@/components/SearchTable'
import { fetchList, fetchDownload, fetchDownloadStructure, fetchDelete } from '@/api/project'
import { ElMessage, ElLoading } from 'element-plus'
import { useRouter } from 'vue-router'

const router = useRouter()
const statusEnum = {
  1: '内容录入',
  2: '要点提纲',
  3: '初版生成',
  4: '确稿留存'
}

const SearchTableRef = ref(null)

const tableColumns = reactive([
  // {
  //   prop: 'id',
  //   label: 'id',
  //   search: {
  //     hide: true
  //   }
  // },
  {
    prop: 'projectName',
    label: '名称',
    resizable: true,
  },
  {
    prop: 'projectCreator',
    label: '发明人',
    width: '120px',
  },
  {
    prop: 'projectTags',
    label: '技术领域',
    resizable: true,
  },
  {
    prop: 'status',
    label: '项目状态',
    resizable: true,
    type: 'select',
    formatter: (record, column, v) => {
      return statusEnum[v] || '-'
    },
    search: {
      options: Object.keys(statusEnum).map(k => ({ label: statusEnum[k], value: k})),
    }
  },
  {
    prop: 'create_time',
    label: '创建时间',
    type: 'date',
    sortable: 'custom',
    resizable: true,
    formatter: (record) => record.createTime || '-',
    search: {
      type: 'daterange',
      transform: (v) => ({
        beginCreateTime: v[0].getTime(),
        endCreateTime: v[1].getTime() + 24 * 3600000 - 1
      })
    }
  },
  {
    prop: 'action',
    label: '操作',
    width: '190px',
    slot: true,
    search: { hide: true }
  }
])

const handleWriting = (id) => {
  router.push(`/writing?id=${id}`)
}

const handlePreview = (item) => {
  window.open(item.previewUrl)
}

const promiseDownload = (promise, download) => {
  const loading = ElLoading.service({
    lock: true,
    text: '正在下载...',
    background: 'rgba(255, 255, 255, 0.7)'
  })

  promise
    .then(blob => {
      if (blob) {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        ElMessage.success('下载成功')
        loading.close()
      } else {
        loading.close()
      }
    })
    .catch(() => {
      loading.close()
    })
}

const handleDownload = (item) => {
  const files = []
  let download = ''
  if (item.isProject) {
    const uploadFiles = []
    const resultFiles = []
    item.uploadFiles?.forEach(f => {
      uploadFiles.push(f.fileUrl)
    })
    item.resultFiles?.forEach(f => {
      resultFiles.push(f.fileUrl)
    })
    if (!uploadFiles.length && !resultFiles.length) {
      ElMessage.warning('项目暂无文件')
      return
    }
    const urls = {
      '上传文件': uploadFiles,
      '终稿文件': resultFiles
    }

    download = `${item.projectName}.zip`
    promiseDownload(fetchDownloadStructure({ urls }), download)
    return
  }
  if (!item.isLeaf) {
    item.children.forEach(f => {
      files.push(f.fileUrl)
    })
    download = `${item.parentName ? `${item.parentName}-` : ''}${item.projectName}.zip`
    promiseDownload(fetchDownloadStructure({ urls: {[item.projectName]: files} }), download)
  } else {
    files.push(item.fileUrl)
    download = item.fileName
    promiseDownload(fetchDownload(files), download)
  }
}

const handleDelete = (id) => {
  fetchDelete([id]).then(res => {
    if (res) {
      ElMessage.success('删除成功')
      SearchTableRef.value.delete()
    }
  })
}

const getRows = (rows) => {
  return rows.map(row => {
    const children = []

    if (row.uploadFiles?.length) {
      children.push({
        id: row.id + 'uploadFiled',
        projectName: '上传文件',
        parentName: row.projectName,
        children: row.uploadFiles.map(v => ({
          ...v,
          projectName: v.fileName,
          projectCreator: v.createBy,
          isLeaf: true
        }))
      })
    }
    if (row.resultFiles?.length) {
      children.push({
        id: row.id + 'resultFiles',
        projectName: '终稿文件',
        parentName: row.projectName,
        children: row.resultFiles.map(v => ({
          ...v,
          projectName: v.fileName,
          projectCreator: v.createBy,
          isLeaf: true
        }))
      })
    }
    return { ...row, isProject: true, children }
  })
}

</script>

<style lang="scss" scoped>
.project {
  width: 100%;
  height: 100%;
  padding: 3% 6%;
  box-sizing: border-box;
  :deep(.el-table) {
    .el-table__header {
      .el-table__cell {
        font-weight: bold;
      }
    }
  }
}
</style>